import {makeAutoObservable, observable} from 'mobx'
import React from 'react'
import {Lang} from 'yougile-lang'
import {ObjUtil, saveAsXlsx, SearchUtil, UIUtil} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {DirectMessages} from '@client/direct-messages'
import {EventBus} from '@client/event-bus'
import {openInvitePopup} from '@client/events'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {createUseStore, noPropagate} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {UserAccess} from '@extensions/user-access'
import {UserUtils} from '@extensions/user-utils'
import {
  getAdmins,
  getDepartments,
  getNotDistributed,
  getPosition,
  getUsers,
  isAdmin
} from '../dashboard-utils'
import {extractUserFields, UserFields} from '../user-fields'

interface State {
  filterQuery: string
  maxShow: number
  filtered: UserFields[]
  notDistributedUsers: UserFields[]
  distributedUsers: UserFields[]
  isInvitePopupOpened: boolean
  isInviteRoleUserPopupOpened: boolean
}

export class UsersStore {
  state: State = {
    filterQuery: '',
    maxShow: 20,
    filtered: [],
    notDistributedUsers: [],
    distributedUsers: [],
    isInvitePopupOpened: false,
    isInviteRoleUserPopupOpened: false
  }

  constructor() {
    makeAutoObservable(this, {
      state: observable.deep
    })
  }

  showMore(e: React.MouseEvent): void {
    noPropagate(e)
    this.state.maxShow += 50
  }

  startChat(e: React.MouseEvent, userId: DataObjectId): void {
    noPropagate(e)
    void Tracking.send(TrackingEvent.chatOpenDirectFromUserList)
    ChatUtils.openChat(DirectMessages.requestChatWith(userId))
  }

  toggleAdmin(userId: DataObjectId): void {
    void Accounts.toggleAdmin(globals.getCompanyId(), userId)
  }

  setInvitePopupOpen(open: boolean): void {
    this.state.isInvitePopupOpened = open
    if (open) {
      void Tracking.send(
        TrackingEvent.myCompanyCompanyMembersClickOnInviteUsers
      )
    }
  }
  setInviteRoleUserPopupOpen(open: boolean): void {
    this.state.isInviteRoleUserPopupOpened = open
  }

  async delete(userId: DataObjectId): Promise<void> {
    if (!isAdmin()) {
      MessageBox.show('Operation is unavailable', 'danger')
    } else {
      const box = Sync.getCurrentSandbox()
      const admins = getAdmins()

      if (admins.length === 1 && admins[0].id === userId) {
        MessageBox.show(Lang.msg.removeLastAdminWarn, 'danger')
        return
      }
      if (await showDeleteConfirm(Lang.msg.deleteUser)) {
        UserAccess.removeUserFromCompany(userId, box)
      }
    }
  }

  getFiltered(): UserFields[] {
    const box = Sync.getCurrentSandbox()
    const all = UserUtils.sortByName(box.getAllUsers(false))
    const words = SearchUtil.prepareWords(
      SearchUtil.cleanStr(this.state.filterQuery)
    )
    const res: UserFields[] = []
    all.forEach(u => {
      const name = SearchUtil.cleanStr(Accounts.getName(u.id))
      if (SearchUtil.hasAllWords(name, words)) {
        res.push(extractUserFields(u))
        return
      }
      const departments = SearchUtil.cleanStr(getDepartments(u))
      if (SearchUtil.hasAllWords(departments, words)) {
        res.push(extractUserFields(u))
        return
      }
      const position = SearchUtil.cleanStr(getPosition(u))
      if (SearchUtil.hasAllWords(position, words)) {
        res.push(extractUserFields(u))
        return
      }
      const email = SearchUtil.cleanStr(u.email)
      if (SearchUtil.hasAllWords(email, words)) {
        res.push(extractUserFields(u))
        return
      }
    })
    return res
  }

  get allNotDistributedUsersCount(): number {
    return this.state.notDistributedUsers.length
  }

  get allDistributedUsersCount(): number {
    return this.state.distributedUsers.length
  }

  get allUsersCount(): number {
    return this.state.filterQuery
      ? this.state.filtered.length
      : this.allNotDistributedUsersCount + this.allDistributedUsersCount
  }

  open(event: React.MouseEvent, userId?: DataObjectId): void {
    noPropagate(event)
    if (UIUtil.isConsumed(event as unknown as Event)) {
      return
    }
    const user = userId ? Sync.getCurrentSandbox().getUser(userId) : undefined
    EventBus.dispatch(openInvitePopup(user))
  }

  downloadPermissionsXlsx(e: React.MouseEvent): void {
    noPropagate(e)
    const box = Sync.getCurrentSandbox()
    const allProjects = [
      ...Project.listProjects(box.companyId, 'not-archive', false).sort(
        Project.projectOrderComparer
      ),
      ...Project.listProjects(box.companyId, 'archive', false).sort(
        Project.projectOrderComparer
      )
    ]

    const data: string[][] = [['', ...allProjects.map(p => p.title)]]
    const all = UserUtils.sortByName(box.getAllUsers(false))
    all.forEach(u => {
      data.push([
        Accounts.getName(u.id),
        ...allProjects.map(p => {
          const key = UserAccess.getRoleInProject(u.id, p)
          if (!key) {
            return ''
          }
          const customRoles = ObjUtil.values(p.data.roles ?? {})

          if (key === 'admin') {
            return Lang.msg.permission.adminRole
          }
          if (key === 'worker') {
            return Lang.msg.permission.workerRole
          }
          if (key === 'observer') {
            return Lang.msg.permission.observerRole
          }
          return customRoles.find(r => r.key === key)?.name ?? ''
        })
      ])
    })
    saveAsXlsx('roles-in-projects.xlsx', data)
  }

  downloadXlsx(e: React.MouseEvent): void {
    noPropagate(e)
    const box = Sync.getCurrentSandbox()
    const msg = Lang.msg.users
    const data: string[][] = [
      [
        msg.headerName,
        msg.headerEmail,
        msg.headerPosition,
        msg.headerDepartments,
        msg.headerAdmin
      ]
    ]
    const all = UserUtils.sortByName(box.getAllUsers(false))

    all.forEach(u => {
      const fields = extractUserFields(u)
      data.push([
        fields.name,
        fields.email,
        fields.position,
        fields.departments,
        fields.isAdmin ? msg.tableYes : msg.tableNo
      ])
    })
    saveAsXlsx('users.xlsx', data)
  }

  changeFilterQuery(value: string): void {
    this.state.filterQuery = value
  }

  calculateFiltered(): void {
    this.state.filtered = this.getFiltered()
  }

  calculateNotDistributed(): void {
    this.state.notDistributedUsers = getNotDistributed()
  }

  calculateUsers(): void {
    this.state.distributedUsers = [...getAdmins(), ...getUsers()]
  }

  calculateLists(): void {
    this.calculateNotDistributed()
    this.calculateUsers()
  }
}

const [CompanyUsersContext, useCompanyUsersStore] = createUseStore(UsersStore)
export {CompanyUsersContext, useCompanyUsersStore}
